import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, AlertTriangle, Calendar, Users, ScanLine, Loader2 } from 'lucide-react';

/**
 * Phase 323 — Public Attendance Scan Landing
 * ------------------------------------------
 * URL: /attend/:token
 *
 * Mobile-first single-page form. No auth. Token carries academy_id,
 * batch, date, kind. The form takes the user's phone (or staff ID) and
 * fires the public check-in endpoint.
 */
const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function PublicAttendanceScan() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [phone, setPhone] = useState('');
  const [staffId, setStaffId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/attendance-scan/${token}`);
        if (!cancelled) setMeta(data);
      } catch (err) {
        if (!cancelled) setMetaError(err.response?.data?.detail || 'Invalid or expired QR code');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim() && !staffId.trim()) {
      setSubmitError('Enter your phone number or staff ID');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {};
      if (phone.trim()) body.phone = phone.trim();
      if (staffId.trim()) body.custom_id = staffId.trim();
      const { data } = await axios.post(`${API_BASE}/public/attendance-scan/${token}/checkin`, body);
      setResult(data);
      if (!data.already_marked) {
        confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ['#10b981', '#059669', '#fbbf24'] });
      }
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-white p-4 flex items-center justify-center">
        <Card className="w-full max-w-sm">
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-2/3 mx-auto" />
            <Skeleton className="h-4 w-1/2 mx-auto" />
            <Skeleton className="h-24 w-full mt-4" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (metaError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-amber-50 p-4 flex items-center justify-center">
        <Card className="w-full max-w-sm" data-testid="attendance-scan-error">
          <CardContent className="p-6 text-center space-y-2">
            <AlertTriangle className="h-10 w-10 text-rose-500 mx-auto" />
            <h1 className="text-lg font-bold text-slate-900">QR Code Invalid</h1>
            <p className="text-sm text-slate-600">{metaError}</p>
            <p className="text-xs text-slate-400 mt-3">Ask your coach for a fresh attendance QR.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-white p-4 flex items-center justify-center">
        <Card className="w-full max-w-sm border-emerald-300" data-testid="attendance-scan-success">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h1 className="text-xl font-bold text-emerald-700">
              {result.already_marked ? 'Already Marked!' : 'You\'re In!'}
            </h1>
            <p className="text-sm text-slate-700 font-semibold">{result.name}</p>
            <div className="text-xs text-slate-500 space-y-0.5">
              <p>{meta.academy_name}</p>
              <p>{result.batch} · {meta.date}</p>
              {result.checked_in_at && (
                <p>Checked in at {new Date(result.checked_in_at).toLocaleTimeString()}</p>
              )}
            </div>
            {result.already_marked && (
              <p className="text-xs text-amber-600 mt-2">You were already marked present for this session.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-white p-4 flex items-center justify-center" data-testid="attendance-scan-page">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6 space-y-4">
          <div className="text-center space-y-1">
            <div className="mx-auto w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center">
              <ScanLine className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900">{meta.academy_name}</h1>
            <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
              <Users className="h-3 w-3" /> {meta.batch}
              <span className="text-slate-300">·</span>
              <Calendar className="h-3 w-3" /> {meta.date}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="attendance-scan-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Phone Number</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Phone (yours or parent's)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="attendance-scan-phone-input"
                autoFocus
              />
            </div>
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-wide"><span className="bg-white px-2 text-slate-400">or</span></div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Student / Staff ID</Label>
              <Input
                placeholder="e.g. STUD009"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                data-testid="attendance-scan-customid-input"
              />
            </div>

            {submitError && (
              <p className="text-xs text-rose-600" data-testid="attendance-scan-error-text">{submitError}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={submitting}
              data-testid="attendance-scan-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Marking…</> : 'Mark Me Present'}
            </Button>
          </form>

          <p className="text-[10px] text-slate-400 text-center">
            Out-Play.in · attendance kiosk
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
